// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CategoryItem from "@bg/App/Components/CategoryItem";
import { CustomAppState, xHeaderActions, xHeaderState } from "@bg/App/Store/Reducers";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    title = "title",
    showAsCategoryTiles = "showAsCategoryTiles",
}

interface LinkModel {
    fields: {
        destination: LinkFieldValue;
        imageUrl: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.title]: string;
        [fields.showAsCategoryTiles]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({});

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & DispatchProp<xHeaderActions>;

const CategoryListL1L2: React.FC<Props> = ({ fields: { links, showAsCategoryTiles, title } }) => {
    const categories = useGetLinks(links, o => o.fields.destination);

    return (
        <>
            {showAsCategoryTiles ? (
                <StyledWrapper
                    css={css`
                        width: 100%;
                        display: flex;
                        justify-content: flex-start;
                        aling-items: flex-start;
                        flex-wrap: wrap;
                    `}
                >
                    <GridContainer gap={30}>
                        {categories.map((category, index) => (
                            <GridItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                width={[12, 6, 4, 3, 3]}
                            >
                                <CategoryItem
                                    name={category.title}
                                    path={category.url}
                                    smallImagePath={links[index].fields.imageUrl}
                                />
                            </GridItem>
                        ))}
                    </GridContainer>
                </StyledWrapper>
            ) : (
                <StyledWrapper
                    css={css`
                        padding-top: 30px;
                        padding-bottom: 11px;
                        border-top: 2px solid ${getColor("common.ui3")};
                        border-bottom: 2px solid ${getColor("common.ui3")};
                    `}
                >
                    <Typography
                        size={22}
                        weight={500}
                        lineHeight="22px"
                        css={css`
                            margin-bottom: 15px;
                            display: block;
                        `}
                    >
                        {title}
                    </Typography>
                    <GridContainer gap={0}>
                        {categories.map((category, index) => (
                            <GridItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                width={12}
                            >
                                <Link
                                    href={category.url}
                                    css={css`
                                        font-size: 15px;
                                        line-height: 32px;
                                        font-weight: 500;

                                        &,
                                        span {
                                            letter-spacing: 0;
                                        }
                                    `}
                                >
                                    {category.title}
                                </Link>
                            </GridItem>
                        ))}
                    </GridContainer>
                </StyledWrapper>
            )}
        </>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Category List - L1 & L2",
    fieldDefinitions: [
        {
            name: fields.showAsCategoryTiles,
            displayName: "Show as Category Tiles",
            tooltip: "if it's not checked, it will show as Link List",
            fieldType: "General",
            editorTemplate: "CheckboxField",
            defaultValue: true,
        },
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isVisible: ({ fields }) => !fields.showAsCategoryTiles,
        },
        {
            name: fields.links,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields, state) => {
                const { type, value } = item.fields.destination;
                const { overrideTitle, title } = item.fields;
                if (type === "Page") {
                    const link = getPageLinkByNodeId(state, value);
                    return overrideTitle || link?.title;
                }
                if (type === "Category") {
                    if (overrideTitle) {
                        return overrideTitle;
                    }
                    if (value === emptyGuid) {
                        return "Products";
                    }
                    const categoryState = getCategoryState(state, value);
                    if (!categoryState.value) {
                        if (!categoryState.isLoading && value) {
                            return dispatch => {
                                dispatch(
                                    loadCategory({
                                        id: value,
                                    }),
                                );
                            };
                        }
                        return "";
                    }
                    return categoryState.value.shortDescription;
                }
                if (type === "Url") {
                    return title ?? value;
                }
                return value;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Page",
                    },
                    isRequired: true,
                },
                {
                    name: "imageUrl",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isRequired: false,
                    tooltip: "you only need to author this in case you want to show category tiles",
                    isVisible: ({ fields }) => !fields.showAsCategoryTiles,
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CategoryListL1L2),
    definition,
};

export default widgetModule;
